import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';

import { AnalyticsService } from '@/services';
import {
  selectReportDateRange,
  useAppSelector,
  useDownloadLearningTrendReportCsvMutation,
  useFetchLearningTrendReportQuery,
} from '@/store';
import { CsvVariants } from '@/types';

import { styles } from '../../styles';
import { EmptyChartView, EmptyViewImageKeys } from '../EmptyChartView';
import { TileHeader } from '../TileHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  PointElement,
  LineController,
  LineElement,
  ChartDataLabels,
);

export type BarLineChartData = ChartData<'bar' | 'line', number[]>;

const chartConfig: ChartOptions<'bar' | 'line'> = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
      filter: context => {
        return context.dataset.type === 'line';
      },
      mode: 'nearest' as const,
      intersect: false,
      callbacks: {
        title: () => '',
        label: context => context.parsed.y?.toString(),
      },
    },
  },
  responsive: true,
  scales: {
    y: {
      display: true,
      ticks: {
        maxTicksLimit: 6,
      },
      grace: '20%',
    },
    x: {
      display: true,
      grid: {
        display: false,
      },
    },
  },
};

export const LearningTrend = () => {
  const { palette } = useTheme();
  const chartColors = useMemo(
    () => ({
      activeUsers: palette.charts?.reports?.users,
      startedEpisodes: palette.charts?.reports?.started,
      complianceEarnedEpisodes: palette.charts?.reports?.earned,
    }),
    [palette],
  );

  const dateRange = useAppSelector(selectReportDateRange);
  const { data, isFetching } = useFetchLearningTrendReportQuery(
    { dateRange },
    {
      skip: !dateRange.fromDate || !dateRange.toDate,
    },
  );

  const chartData: BarLineChartData = useMemo(() => {
    if (!data) return { labels: [], datasets: [] };

    return {
      labels: data.map(({ date, grainSize }) =>
        new Date(date).toLocaleString('default', {
          month: 'short',
          ...(grainSize === 'month' ? { year: 'numeric' } : { day: 'numeric' }),
        }),
      ),
      datasets: [
        {
          label: 'Active Users',
          data: data.map(({ users }) => users),
          type: 'line' as const,
          borderColor: chartColors.activeUsers,
          order: 1,
          pointStyle: false,
          datalabels: {
            display: false,
          },
        },
        {
          label: 'Started Episodes',
          type: 'bar' as const,
          data: data.map(({ startedEpisodes }) => startedEpisodes),
          borderRadius: 4,
          backgroundColor: chartColors.startedEpisodes,
          order: 2,
          datalabels: {
            anchor: 'end',
            align: 'end',
          },
        },
        {
          label: 'Compliance-Earned Episodes',
          type: 'bar' as const,
          data: data.map(
            ({ complianceEarnedEpisodes }) => complianceEarnedEpisodes,
          ),
          borderRadius: 4,
          backgroundColor: chartColors.complianceEarnedEpisodes,
          order: 2,
          datalabels: {
            anchor: 'end',
            align: 'end',
          },
        },
      ],
    };
  }, [data, chartColors]);

  const [downloadCsv, { isLoading: isDownloadingCsv }] =
    useDownloadLearningTrendReportCsvMutation();

  const isEmpty = useMemo(() => {
    if (!data) return false;
    return data.every(
      ({ users, startedEpisodes, complianceEarnedEpisodes }) =>
        users === 0 && startedEpisodes === 0 && complianceEarnedEpisodes === 0,
    );
  }, [data]);

  return (
    <Box sx={[styles.tile, styles.tileWithMinHeight, styles.column]}>
      <TileHeader
        title="Learning Trend"
        description="Tracks changes in learning activity and active users across the selected time period."
        buttonProps={{
          onClick: () => {
            downloadCsv({ dateRange });
            AnalyticsService.downloadReportingCsv({
              reportType: CsvVariants.LEARNING_TREND,
            });
          },
          isLoading: isDownloadingCsv,
        }}
        grow={!isEmpty}
      />
      {isFetching && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <CircularProgress />
        </Box>
      )}
      {!isFetching && isEmpty && (
        <EmptyChartView
          title={EMPTY_CHART_VIEW_TITLE}
          subtitle={EMPTY_CHART_VIEW_SUBTITLE}
          imageKey={EmptyViewImageKeys.books}
        />
      )}
      {!isFetching && !isEmpty && (
        <>
          <Box>
            <Box sx={{ height: 280, mb: 2 }}>
              <Chart
                type={'bar'}
                options={chartConfig}
                data={chartData}
                width={'100%'}
                height={'100%'}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                '& > div': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}>
              <Box>
                <Box
                  sx={{
                    height: 2,
                    width: 20,
                    backgroundColor: chartColors.activeUsers,
                    mr: 1,
                  }}
                />
                <Typography variant="body3">Active Users</Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    height: 12,
                    width: 12,
                    backgroundColor: chartColors.startedEpisodes,
                    borderRadius: 0.5,
                    mr: 1,
                  }}
                />
                <Typography variant="body3">Started Episodes</Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    height: 12,
                    width: 12,
                    backgroundColor: chartColors.complianceEarnedEpisodes,
                    borderRadius: 0.5,
                    mr: 1,
                  }}
                />
                <Typography variant="body3">
                  Compliance-Earned Episodes
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const EMPTY_CHART_VIEW_TITLE = 'Nothing to track yet';
const EMPTY_CHART_VIEW_SUBTITLE =
  "Encourage your team to complete episodes and you'll start seeing monthly trends here.";
